import {Divider, Stack, Typography} from '@mui/material';
import {isValid} from 'date-fns';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AlertInfo,
	CTAButtonFixed,
	MainFooter,
	MaskedInput,
	TextInput,
} from 'presentation/components';
import ModalDatePicker from 'presentation/components/ModalDatePicker';
import Icon from 'presentation/icons';
import {useConsultRefund} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {masks, parseDate} from 'presentation/utils';
import React, {Fragment, useState} from 'react';
import {FormProvider} from 'react-hook-form';
import {RefundByDateSchema, RefundByProtocolSchema} from './schema';
import {StyledFooterContainer, StyledForm, StyledLabel, StyledStack} from './styles';
import {InitialStepProps} from './types';

const {search, initialAndEndDate, selectDates, protocolNumber, or} = translations['pt-br'].commons;

const {consultRefund, consultPreviewRefund} = translations['pt-br'].privateFlow;

export function InitialStep({variant}: InitialStepProps) {
	const {
		handleClearInput,
		methods,
		handleSearchByDate,
		handleSearchByProtocol,
		handleSetDates,
		searchByProtocol,
		shouldShowAlertError,
	} = useConsultRefund();

	const [openDatePickerState, setOpenDatePickerState] = useState<boolean>(false);
	const [showAlertErrorState, setShowAlertErrorState] = useState<boolean>(false);

	const {
		handleSubmit,
		watch,
		register,
		formState: {errors},
	} = methods;

	const rangeDateWatched = watch('rangeDate');
	const initialDateWatched = parseDate(rangeDateWatched.split(' - ')[0]);
	const endDateWatched = parseDate(rangeDateWatched.split(' - ')[1]);

	const isPreview = variant === 'preview';

	const formDescription = isPreview
		? consultPreviewRefund.formDescription
		: consultRefund.formDescription;

	async function handleSearch(data: RefundByProtocolSchema | RefundByDateSchema) {
		if (shouldShowAlertError) {
			setShowAlertErrorState(true);
			return;
		}

		setShowAlertErrorState(false);

		if (searchByProtocol) {
			handleSearchByProtocol({
				protocolNumber: (data as RefundByProtocolSchema).protocolNumber,
			});
		} else {
			handleSearchByDate({
				rangeDate: (data as RefundByDateSchema).rangeDate,
				isPreview: variant === 'preview',
			});
		}
	}

	return (
		<Fragment>
			<StyledForm onSubmit={handleSubmit(handleSearch)}>
				<StyledStack>
					<Typography color="text.primary">{formDescription}</Typography>
					{showAlertErrorState ? (
						<AlertInfo.Root
							sx={{borderRadius: 0.5}}
							icon={<Icon name="error-outline" />}
							severity="error">
							<AlertInfo.Description>
								{consultRefund.consultError}
							</AlertInfo.Description>
						</AlertInfo.Root>
					) : null}
					<FormProvider {...methods}>
						{!isPreview ? (
							<Fragment>
								<Stack spacing={1}>
									<StyledLabel htmlFor="protocol-number">
										{protocolNumber}
									</StyledLabel>
									<TextInput
										handleClearQuery={handleClearInput}
										endAdornmentVariant="close"
										slotProps={{htmlInput: {id: 'protocol-number'}}}
										placeholder={consultRefund.formRefundNumberPlaceholder}
										{...register('protocolNumber')}
										helperText={errors.protocolNumber?.message}
										error={!!errors.protocolNumber}
									/>
								</Stack>
								<Divider sx={{fontSize: '0.75rem', color: 'text.secondary'}}>
									{or}
								</Divider>
							</Fragment>
						) : null}

						<Stack spacing={1} flex={1}>
							<StyledLabel htmlFor="initial-date">{initialAndEndDate}</StyledLabel>
							<MaskedInput
								{...register('rangeDate')}
								id="rangeDate"
								inputMode="text"
								error={!!errors.rangeDate}
								helperText={errors.rangeDate?.message}
								fullWidth
								autoComplete="current-rangeDate"
								label={selectDates}
								placeholder={'DD/MM/AAAA - DD/MM/AAAA'}
								$fieldName="rangeDate"
								maskOptions={masks.datePicker}
								endAdornmentVariant="button"
								endAdornmentIcon="calendar-focused-filled"
								dataTagId={
									!isPreview
										? RefundTaggingEnum.CLICK_ABRIR_CALENDARIO_CONSULTA_REEMBOLSO
										: undefined
								}
								handleClearQuery={handleClearInput}
								isRefundFlow
								handleAction={() => setOpenDatePickerState(true)}
							slotProps={{
								inputLabel: {
									shrink: true,
								},
							}}
							/>
						</Stack>
						<ModalDatePicker
							dataTagId={{
								closeButton: !isPreview
									? RefundTaggingEnum.CLICK_FECHAR_CALENDARIO_CONSULTA_REEMBOLSO
									: undefined,
								confirmButton: !isPreview
									? RefundTaggingEnum.CLICK_CONFIRMAR_DATA_CONSULTA_REEMBOLSO
									: undefined,
								clearButton: !isPreview
									? RefundTaggingEnum.CLICK_LIMPAR_DATA_CONSULTA_REEMBOLSO
									: undefined,
							}}
							externalOpen={openDatePickerState}
							onAbandonModal={() => setOpenDatePickerState(false)}
							selectFirstDate={() => null}
							selectSecondDate={() => null}
							selectDates={handleSetDates}
							currentFirstDate={
								isValid(initialDateWatched) ? initialDateWatched : undefined
							}
							currentSecondDate={isValid(endDateWatched) ? endDateWatched : undefined}
							includeDateIntervals={{
								start: new Date(1900, 1, 1),
								end: new Date(),
							}}
							showClearButton
							hideOpenButton
							isSecondDateOptional
							shouldHaveDateIntervals
						/>
					</FormProvider>
				</StyledStack>
				<CTAButtonFixed.Root alignSelf="flex-end">
					<CTAButtonFixed.Action
						type="submit"
						size="large"
						data-tag-id={
							isPreview
								? RefundTaggingEnum.CLICK_PESQUISAR_CONSULTA_PREVIA_REEMBOLSO
								: RefundTaggingEnum.CLICK_PESQUISAR_CONSULTAR_REEMBOLSO
						}>
						{search}
					</CTAButtonFixed.Action>
				</CTAButtonFixed.Root>
			</StyledForm>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
